import { Box, IconButton, Skeleton, Typography, useTheme } from '@mui/material';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DraggableProvided,
  DraggableStateSnapshot,
  DropResult,
} from '@hello-pangea/dnd';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import StarIcon from '@mui/icons-material/Star';
import BlockIcon from '@mui/icons-material/Block';
import RestoreIcon from '@mui/icons-material/Restore';

export interface PageReorderGridProps {
  /** 1-indexed permutation of [1..page_count]. Length defines tile count. */
  pageOrder: number[];
  onReorder: (next: number[]) => void;
  /** Page image URL keyed by ORIGINAL PDF page number (1-indexed). */
  pageImages: Record<number, string | undefined>;
  /** True while the page images are still being fetched. */
  imagesLoading?: boolean;
  /** Per-page rotation in degrees, keyed by original PDF page number. */
  rotations?: Record<number, number>;
  /** Rotate a page. `delta` is -90 (left) or 90 (right). */
  onRotate?: (pageNumber: number, delta: -90 | 90) => void;
  /** 1-indexed visible-position boundaries (a new child starts at each). */
  boundaries?: number[];
  onToggleBoundary?: (visiblePosition: number) => void;
  /** Open the full-page preview for this original PDF page. */
  onView?: (pageNumber: number) => void;
  /** Original PDF page numbers marked for omission from every child. */
  omittedPages?: Set<number>;
  onToggleOmit?: (pageNumber: number) => void;
  /** Designate a page as a cover page (moves it to the cover row). */
  onDesignateCover?: (pageNumber: number) => void;
  /** Tile size hint. */
  tileSize?: 'default' | 'large';
  /** Cover count, so visible slot numbers continue after the covers. */
  slotIndexOffset?: number;
}

/** Reorder a list immutably moving the item at `from` to `to`. */
function reorder<T>(list: T[], from: number, to: number): T[] {
  const next = [...list];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

/**
 * Full-width grid of large page thumbnails for drag-drop reorder. Used by the
 * Split page. Each tile is draggable; dropping on another tile inserts the
 * dragged page at that index. Original PDF page numbers appear on each tile so
 * the operator can verify which page is where.
 *
 * Ported from glynt-ui-next's PageReorderGrid: native HTML5 drag-drop →
 * @hello-pangea/dnd, shadcn/Tailwind → MUI, per-page image fetch → image URLs
 * supplied by the parent (legacy useDocumentAllPages pattern).
 */
export function PageReorderGrid({
  pageOrder,
  onReorder,
  pageImages,
  imagesLoading = false,
  rotations,
  onRotate,
  boundaries,
  onToggleBoundary,
  onView,
  omittedPages,
  onToggleOmit,
  onDesignateCover,
  tileSize = 'default',
  slotIndexOffset = 0,
}: PageReorderGridProps) {
  const minTile = tileSize === 'large' ? '16.5rem' : '13rem';

  function handleDragEnd(result: DropResult) {
    if (!result.destination) return;
    if (result.source.index === result.destination.index) return;
    onReorder(reorder(pageOrder, result.source.index, result.destination.index));
  }

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable droppableId="page-reorder-grid" direction="horizontal">
        {(provided) => (
          <Box
            ref={provided.innerRef}
            {...provided.droppableProps}
            data-cy="page-reorder-grid"
            data-tile-size={tileSize}
            sx={{
              display: 'grid',
              gridTemplateColumns: `repeat(auto-fill, minmax(${minTile}, 1fr))`,
              gap: 2,
              p: 1,
              alignItems: 'start',
            }}
          >
            {pageOrder.map((pageNumber, idx) => {
              const isLast = idx === pageOrder.length - 1;
              const nextPosition = idx + 2; // boundary "after this tile"
              const boundaryActive = !!boundaries?.includes(nextPosition);
              return (
                <Draggable
                  key={pageNumber}
                  draggableId={`page-${pageNumber}`}
                  index={idx}
                >
                  {(dp: DraggableProvided, snap: DraggableStateSnapshot) => (
                    <ReorderTile
                      provided={dp}
                      isDragging={snap.isDragging}
                      pageNumber={pageNumber}
                      slotIndex={idx}
                      displaySlot={idx + slotIndexOffset + 1}
                      imageUrl={pageImages[pageNumber]}
                      imagesLoading={imagesLoading}
                      rotation={rotations?.[pageNumber] ?? 0}
                      boundaryActive={boundaryActive}
                      isLast={isLast}
                      onToggleBoundary={
                        onToggleBoundary && !isLast
                          ? () => onToggleBoundary(nextPosition)
                          : undefined
                      }
                      onRotate={onRotate ? (delta) => onRotate(pageNumber, delta) : undefined}
                      onView={onView ? () => onView(pageNumber) : undefined}
                      omitted={omittedPages?.has(pageNumber) ?? false}
                      onToggleOmit={onToggleOmit ? () => onToggleOmit(pageNumber) : undefined}
                      onDesignateCover={
                        onDesignateCover ? () => onDesignateCover(pageNumber) : undefined
                      }
                    />
                  )}
                </Draggable>
              );
            })}
            {provided.placeholder}
          </Box>
        )}
      </Droppable>
    </DragDropContext>
  );
}

interface ReorderTileProps {
  provided: DraggableProvided;
  isDragging: boolean;
  pageNumber: number;
  /** Grid-local 0-indexed position; stable base for data-cy ids. */
  slotIndex: number;
  /** 1-indexed slot number to display (continues after cover pages). */
  displaySlot: number;
  imageUrl?: string;
  imagesLoading: boolean;
  rotation: number;
  boundaryActive: boolean;
  isLast: boolean;
  onToggleBoundary?: () => void;
  onRotate?: (delta: -90 | 90) => void;
  onView?: () => void;
  omitted: boolean;
  onToggleOmit?: () => void;
  onDesignateCover?: () => void;
}

function ReorderTile({
  provided,
  isDragging,
  pageNumber,
  slotIndex,
  displaySlot,
  imageUrl,
  imagesLoading,
  rotation,
  boundaryActive,
  isLast,
  onToggleBoundary,
  onRotate,
  onView,
  omitted,
  onToggleOmit,
  onDesignateCover,
}: ReorderTileProps) {
  const theme = useTheme();
  // Quarter rotations need a scale-down to fit a portrait page into the
  // tile's portrait frame; 180° fits as-is.
  const isQuarter = rotation === 90 || rotation === 270;
  const transform = rotation
    ? `rotate(${rotation}deg)${isQuarter ? ' scale(0.72)' : ''}`
    : undefined;

  // Keep button presses from initiating a drag.
  const stopDrag = (e: React.MouseEvent | React.DragEvent) => e.stopPropagation();

  const id = slotIndex + 1;
  const showActions = onRotate || onView || onToggleOmit || onDesignateCover;

  return (
    <Box
      ref={provided.innerRef}
      {...provided.draggableProps}
      {...provided.dragHandleProps}
      data-cy={`reorder-tile-${id}`}
      data-page-number={pageNumber}
      data-rotation={rotation || undefined}
      data-boundary-after={boundaryActive || undefined}
      data-omitted={omitted || undefined}
      sx={{
        position: 'relative',
        cursor: 'grab',
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: omitted ? 'divider' : 'divider',
        borderStyle: omitted ? 'dashed' : 'solid',
        bgcolor: omitted ? 'action.hover' : 'background.paper',
        p: 1,
        transition: 'all 120ms ease',
        opacity: isDragging ? 0.5 : 1,
        ...(boundaryActive && {
          mr: 1,
          borderRight: `4px solid ${theme.palette.primary.main}`,
        }),
        '&:active': { cursor: 'grabbing' },
        '& .tile-hover': {
          opacity: 0,
          transition: 'opacity 120ms ease',
        },
        '&:hover .tile-hover': { opacity: 1 },
        ...(provided.draggableProps.style as object),
      }}
    >
      {/* Slot label */}
      <Box
        sx={{
          position: 'absolute',
          left: 8,
          top: 8,
          zIndex: 2,
          borderRadius: 0.5,
          bgcolor: 'rgba(255,255,255,0.9)',
          px: 0.75,
          py: 0.25,
          fontSize: 12,
          fontWeight: 600,
          lineHeight: 1,
          boxShadow: 1,
        }}
      >
        Slot {displaySlot}
      </Box>
      {/* Page label + badges */}
      <Box
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          zIndex: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: 0.5,
        }}
      >
        <Box
          sx={{
            borderRadius: 0.5,
            bgcolor: 'rgba(255,255,255,0.9)',
            px: 0.75,
            py: 0.25,
            fontSize: 12,
            lineHeight: 1,
            color: 'text.secondary',
            boxShadow: 1,
          }}
        >
          Page {pageNumber}
        </Box>
        {omitted && (
          <Box
            data-cy={`reorder-tile-${id}-omitted-badge`}
            sx={{
              borderRadius: 0.5,
              bgcolor: 'error.main',
              color: 'error.contrastText',
              px: 0.75,
              py: 0.25,
              fontSize: 12,
              fontWeight: 600,
              lineHeight: 1,
              boxShadow: 1,
            }}
          >
            Omitted
          </Box>
        )}
        {rotation ? (
          <Box
            data-cy={`reorder-tile-${id}-rotation-badge`}
            aria-label={`Rotated ${rotation} degrees`}
            sx={{
              borderRadius: 0.5,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              px: 0.75,
              py: 0.25,
              fontSize: 12,
              fontWeight: 600,
              lineHeight: 1,
              boxShadow: 1,
            }}
          >
            {rotation}°
          </Box>
        ) : null}
      </Box>

      {/* Image */}
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          aspectRatio: '3 / 4',
          overflow: 'hidden',
          borderRadius: 1,
        }}
      >
        {imagesLoading ? (
          <Skeleton variant="rectangular" width="100%" height="100%" />
        ) : !imageUrl ? (
          <Typography variant="caption" color="text.secondary">
            No preview
          </Typography>
        ) : (
          <Box
            component="img"
            src={imageUrl}
            alt={`Page ${pageNumber}`}
            draggable={false}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              transform,
              transformOrigin: 'center center',
              transition: 'transform 150ms ease',
              ...(omitted && { opacity: 0.3, filter: 'grayscale(1)' }),
            }}
          />
        )}

        {/* Boundary toggle on the right edge */}
        {!isLast && onToggleBoundary ? (
          <IconButton
            className="tile-hover"
            size="small"
            disableRipple
            onMouseDown={stopDrag}
            onClick={(e) => {
              e.stopPropagation();
              onToggleBoundary();
            }}
            aria-pressed={boundaryActive}
            aria-label={
              boundaryActive
                ? 'Remove boundary after this page'
                : 'Insert boundary after this page'
            }
            title={
              boundaryActive
                ? 'Remove boundary after this page'
                : 'Insert boundary after this page'
            }
            data-cy={`reorder-tile-${id}-boundary-toggle`}
            sx={{
              position: 'absolute',
              right: -14,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 3,
              width: 28,
              height: 28,
              border: '1px solid',
              borderColor: boundaryActive ? 'primary.main' : 'divider',
              bgcolor: boundaryActive ? 'primary.main' : 'background.paper',
              color: boundaryActive ? 'primary.contrastText' : 'text.secondary',
              boxShadow: 1,
              opacity: boundaryActive ? 1 : undefined,
              '&:hover': {
                bgcolor: boundaryActive ? 'primary.dark' : 'background.paper',
                borderColor: 'primary.main',
                color: boundaryActive ? 'primary.contrastText' : 'primary.main',
              },
            }}
          >
            <ContentCutIcon sx={{ fontSize: 16 }} />
          </IconButton>
        ) : null}

        {/* Bottom action overlay */}
        {showActions ? (
          <Box
            className="tile-hover"
            sx={{
              position: 'absolute',
              insetInline: 0,
              bottom: 0,
              display: 'flex',
              justifyContent: 'center',
              gap: 0.5,
              p: 0.5,
            }}
          >
            {onView ? (
              <TileActionButton
                cy={`reorder-tile-${id}-view`}
                label="View page"
                onClick={onView}
                stopDrag={stopDrag}
              >
                <VisibilityIcon sx={{ fontSize: 16 }} />
              </TileActionButton>
            ) : null}
            {onRotate && !omitted ? (
              <TileActionButton
                cy={`reorder-tile-${id}-rotate-left`}
                label="Rotate page left"
                onClick={() => onRotate(-90)}
                stopDrag={stopDrag}
              >
                <RotateLeftIcon sx={{ fontSize: 16 }} />
              </TileActionButton>
            ) : null}
            {onRotate && !omitted ? (
              <TileActionButton
                cy={`reorder-tile-${id}-rotate-right`}
                label="Rotate page right"
                onClick={() => onRotate(90)}
                stopDrag={stopDrag}
              >
                <RotateRightIcon sx={{ fontSize: 16 }} />
              </TileActionButton>
            ) : null}
            {onDesignateCover && !omitted ? (
              <TileActionButton
                cy={`reorder-tile-${id}-designate-cover`}
                label="Designate as cover page"
                onClick={onDesignateCover}
                stopDrag={stopDrag}
              >
                <StarIcon sx={{ fontSize: 16 }} />
              </TileActionButton>
            ) : null}
            {onToggleOmit ? (
              <TileActionButton
                cy={`reorder-tile-${id}-omit-toggle`}
                label={omitted ? 'Restore page' : 'Omit page'}
                onClick={onToggleOmit}
                pressed={omitted}
                stopDrag={stopDrag}
              >
                {omitted ? (
                  <RestoreIcon sx={{ fontSize: 16 }} />
                ) : (
                  <BlockIcon sx={{ fontSize: 16 }} />
                )}
              </TileActionButton>
            ) : null}
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}

/** Small square action button used in the tile hover overlays. */
export function TileActionButton({
  cy,
  label,
  onClick,
  children,
  pressed,
  stopDrag,
}: {
  cy: string;
  label: string;
  onClick: () => void;
  children: React.ReactNode;
  pressed?: boolean;
  stopDrag: (e: React.MouseEvent | React.DragEvent) => void;
}) {
  return (
    <IconButton
      size="small"
      data-cy={cy}
      aria-label={label}
      aria-pressed={pressed}
      title={label}
      onMouseDown={stopDrag}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      sx={{
        width: 28,
        height: 28,
        bgcolor: pressed ? 'primary.main' : 'background.paper',
        color: pressed ? 'primary.contrastText' : 'text.primary',
        boxShadow: 2,
        '&:hover': { bgcolor: pressed ? 'primary.dark' : 'grey.100' },
      }}
    >
      {children}
    </IconButton>
  );
}
